// The backend `address` field is the IDENTITY key — for DID users it's a
// did:… string, never a chain address. Anything on-chain (balance reads,
// deposit targets) must use the proven linked wallet, falling back to the
// backend field only when it actually is a 0x address (SIWE users).
export const resolveWalletAddress = (
  linkedWallet: string | null | undefined,
  backendAddress: string | undefined
): string | undefined => linkedWallet ?? (backendAddress?.startsWith('0x') ? backendAddress : undefined);

// "Wrong wallet connected" detection (ADR-051 D2). A DID user binds ONE wallet
// (eoa_address, immutable per the BFF 409 policy). If they later have a DIFFERENT
// wallet active and sign a trade/claim from it, the AMM `deposit` mints option
// tokens to msg.sender (the connected wallet), while the server credits the uid —
// so on-chain positions split away from the uid-keyed view and a later claim from
// the bound wallet finds no tokens. Every signing entry point consults this to
// block + prompt switching back.
//
// Returns true ONLY when a bound wallet AND a connected wallet both exist and
// differ (case-insensitive). Deliberately false when:
//   - no bound wallet yet (DID pre-link)  → that's the link-onboarding path
//   - not connected                       → that's the connect-onboarding path
// NOTE: this FAILS OPEN — it cannot distinguish "no binding" from "binding not
// loaded yet" (both give linkedWallet == null → false), so it is a backstop and
// never the only check before a chain write. useTradeGate is the fail-closed one;
// that every chain-write signer funnels through it is pinned per-function by
// use-trade-gate.coverage.test.ts rather than asserted here — a comment claiming
// that coverage is exactly what let an ungated signer ship before (the buy panel
// carried "useTradeGate already blocks at the entry" while never calling it).
// Callers must pass a linkedWallet that does NOT track the connected wallet:
// auth-provider resolves SIWE's from the JWT subject (the wallet proven at login),
// never from the live address, since comparing the connected address to itself
// can never fire.
export const isBoundWalletMismatch = (
  linkedWallet: string | null | undefined,
  connectedAddress: string | null | undefined
): boolean => {
  if (!linkedWallet || !connectedAddress) return false;
  return linkedWallet.toLowerCase() !== connectedAddress.toLowerCase();
};

// Should an active session end because the user switched accounts in their wallet?
//
// Only for SIWE sessions, where the wallet IS the identity: once a different
// account is connected, the session belongs to someone else, and every
// uid-keyed view (balance, portfolio) keeps rendering the PREVIOUS account
// because those read the proven wallet rather than the live one. DID sessions
// are excluded — their identity is external and survives a wallet switch, which
// the signing-time guard handles instead.
//
// Deliberately false while unauthenticated or disconnected: a plain disconnect
// is not an identity change, and firing during login (address set before the
// JWT exists) would log the user out of the session they are creating.
export const shouldEndSessionOnWalletChange = (
  sessionKind: string | null | undefined,
  isAuthed: boolean,
  provenWallet: string | null | undefined,
  connectedAddress: string | null | undefined
): boolean => {
  if (sessionKind !== 'siwe' || !isAuthed) return false;
  return isBoundWalletMismatch(provenWallet, connectedAddress);
};

// Short display form for an address: 0x1234…abcdef (first 6 + last 6). Used in
// the "switch to your linked wallet 0x…" prompt and the referral referee list.
export const truncateWallet = (address: string | null | undefined): string => {
  if (!address) return '';
  return address.startsWith('0x') && address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-6)}` : address;
};
